import React, { createContext, useContext, useEffect, useState } from "react";
import {
  User,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  signInWithRedirect,
  getRedirectResult,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, googleProvider } from "../config/firebase";
import { analyticsEvents } from "../hooks/useAnalytics";
import { friendsService } from "../services/friendsService";
import { initializePushNotifications } from "../services/pushNotifications";
import { subscribeToNotifications } from "../services/notificationsSyncService";
import { useNotificationsStore } from "../services/notificationsStore";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  useEffect(() => {
    // Check for redirect result on mount
    getRedirectResult(auth)
      .then(() => {
        // Redirect successful
      })
      .catch(() => {
        // Silent error handling
      });

    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      // Ensure user document exists in Firestore
      if (user) {
        try {
          await friendsService.ensureUserDocument(
            user.uid,
            user.email || '',
            user.displayName || undefined,
            user.photoURL || undefined
          );
          
          // Initialize push notifications for this user
          await initializePushNotifications();

          // Subscribe to notifications from Firestore
          const unsubscribeNotifications = subscribeToNotifications(user.uid);
          
          // Cleanup notifications subscription on logout
          return () => {
            unsubscribeNotifications();
          };
        } catch (error) {
          console.error('Error ensuring user document:', error);
        }
      } else {
        // Clear notifications on logout
        useNotificationsStore.getState().clearNotifications();
      }
      
      setLoading(false);
      setInitialCheckDone(true);
    });

    // Set a timeout to prevent long loading screens
    const timeout = setTimeout(() => {
      if (!initialCheckDone) {
        setLoading(false);
      }
    }, 500);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [initialCheckDone]);

  const signInWithGoogle = async () => {
    try {
      // Try popup first (works on desktop)
      await signInWithPopup(auth, googleProvider);
      analyticsEvents.login('google');
    } catch (error: any) {
      // If popup fails (mobile/blocked), fallback to redirect
      if (
        error.code === "auth/popup-blocked" ||
        error.code === "auth/popup-closed-by-user"
      ) {
        await signInWithRedirect(auth, googleProvider);
        analyticsEvents.login('google');
      } else {
        throw error;
      }
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      analyticsEvents.login('email');
    } catch (error) {
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      analyticsEvents.login('email');
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      analyticsEvents.logout();
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
